// Costos adicionales que reporta el equipo técnico (Eduardo, Víctor) después de la visita: "mufa $8,000", estructura
// elevada, mover un minisplit... Entran al precio (PLAN-NOCHE §2) y a la rentabilidad.
import { z } from 'zod';
import { FechaHora, Id, Pesos, Texto } from './basicos.ts';

/** Lo más que mide la nota de un costo adicional (la pantalla lo dice y no deja escribir más; la API responde 400 con este texto).
 *  Perisur, 29-oct-2026: una nota de 301 a 500 letras daba ERROR 500 al guardar (el historial solo aceptaba 300 en el motivo). */
export const NOTA_COSTO_MAX = 500;

export const CostoAdicional = z.strictObject({
  id: Id,
  concepto: Texto(2, 120),
  cantidad: z.int().min(1).max(10_000),
  /** Lo que paga el cliente por unidad, sin IVA (precio de LISTA: el contado le aplica el 15 % si así se marca). */
  precio_cliente_sin_iva: Pesos,
  /** Lo que le cuesta a Solarix por unidad, sin IVA. null = no se sabe: la rentabilidad lo toma igual al precio y avisa. */
  costo_solarix_sin_iva: Pesos.nullable(),
  /** ¿Le aplica el 15 % de contado? Si no, el motor declara la excepción de descuento que pide el generador. */
  aplica_descuento_contado: z.boolean(),
  /** Quién lo reportó ("Eduardo", "Víctor", el vendedor). */
  reportado_por: Texto(2, 60),
  origen: z.enum(['visita_tecnica', 'vendedor', 'ia']),
  nota: z.string().trim().max(NOTA_COSTO_MAX, `La nota del costo pasa de ${NOTA_COSTO_MAX} letras: acórtala.`).nullable(),
  /** Del catálogo de la tabla (botón rápido): el SERVIDOR pone el costo para Solarix con el tipo de cambio del folio (el
   *  vendedor nunca lo ve) y, si no viene precio, el sugerido. Ausente/null = capturado a mano. */
  catalogo_id: z.string().regex(/^[a-z0-9_-]{2,40}$/).nullable().optional(),
  capturado_por: Id,
  capturado_en: FechaHora,
});
export type CostoAdicional = z.infer<typeof CostoAdicional>;

/** Lo que el vendedor escribe a mano cuando captura los viáticos del equipo técnico sin decir «viáticos». Siempre cuentan
 *  viático(s), hospedaje, casetas y peaje(s). Hotel, comidas, alimentos, traslado y gasolina solo con contexto de la gente
 *  (equipo, técnico, personal, cuadrilla, instaladores, viaje): «Estructura en azotea del hotel», «Canalización estación de
 *  gasolina», «Nave de alimentos» o «Traslado de material» NO son viáticos (verificador del 24-oct). Palabras completas, sin
 *  acentos ni mayúsculas: «alimentador»/«alimentación» (cable) no cuentan, ni «caseta» sola (la caseta de los inversores). */
const PALABRAS_DE_VIATICOS = /viatico|hospedaje|\bcasetas\b|\bpeajes?\b/;
const PALABRAS_CON_CONTEXTO = /\bhotel(es)?\b|\bcomidas?\b|\balimentos?\b|\btraslados?\b|\bgasolina\b/;
const CONTEXTO_DE_GENTE = /\bequipo\b|tecnic|\bpersonal\b|cuadrilla|instalador|\bviaje/;

/** Diego, 24-oct: ¿este costo adicional son los viáticos? El renglón «viaticos» del catálogo (botón rápido) o un concepto
 *  capturado a mano que diga viático(s), con o sin acento, o un sinónimo (PALABRAS_DE_VIATICOS). */
export function esCostoDeViaticos(a: Pick<CostoAdicional, 'concepto' | 'catalogo_id'>): boolean {
  if (a.catalogo_id === 'viaticos') return true;
  const texto = a.concepto.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
  return PALABRAS_DE_VIATICOS.test(texto) || (PALABRAS_CON_CONTEXTO.test(texto) && CONTEXTO_DE_GENTE.test(texto));
}

/** Verificador del 2-nov (z68-1, «nunca cambies precios que Diego no decidió»): el renglón `viaticos` del catálogo. Es lo ÚNICO que el PDF de todas le puede quitar a una
 *  sucursal: `esCostoDeViaticos` adivina por el texto («Traslado de equipo y grúa» cuenta) y sirve para pedir o avisar, nunca para decidir un precio. */
export function esViaticoDeCatalogo(a: Pick<CostoAdicional, 'catalogo_id'>): boolean {
  return a.catalogo_id === 'viaticos';
}

/** Los conceptos que se contaron como viáticos (para que el aviso diga cuáles: Diego ve qué quitó el rojo). */
export function conceptosDeViaticos(costos: readonly Pick<CostoAdicional, 'concepto' | 'catalogo_id' | 'precio_cliente_sin_iva' | 'cantidad'>[]): string[] {
  return costos.filter((a) => esCostoDeViaticos(a) && a.precio_cliente_sin_iva * a.cantidad > 0).map((a) => a.concepto);
}

/** Lo que suman los viáticos capturados (precio al cliente sin IVA × cantidad). 0 = no hay viáticos con monto. */
export function montoDeViaticos(costos: readonly Pick<CostoAdicional, 'concepto' | 'catalogo_id' | 'precio_cliente_sin_iva' | 'cantidad'>[]): number {
  return Math.round(costos.filter(esCostoDeViaticos).reduce((s, a) => s + Math.max(0, a.precio_cliente_sin_iva) * a.cantidad, 0) * 100) / 100;
}

// ───────────── ADENDA z46-7 (P19, Diego 27-oct): UN viático por ciudad en un proyecto de varias sucursales ─────────────

/** ¿Esta sucursal reparte sus viáticos por ciudad (P19)? Solo si nació con el parámetro de versión `viatico_por_ciudad` (proyecto
 *  multisitio NUEVO). Sin él (ausente o false) es de un proyecto guardado antes: cada sucursal fuera de Hermosillo lleva los suyos,
 *  como en producción. Lo leen el servidor (al calcular cada sucursal y en el PDF de todas) y sus pruebas. */
export function viaticoPorCiudadAplica(p: { viatico_por_ciudad?: boolean | undefined }): boolean {
  return p.viatico_por_ciudad === true;
}

/** ADENDA z67-1 (Diego, 30-oct): ¿cada PDF suelto de esta sucursal lleva su viaje (y el PDF de todas lo cobra una vez por ciudad)?
 *  Solo si nació con el parámetro de versión `viaje_por_sitio` (proyecto multisitio NUEVO). Sin él, como z46-7. */
export function viajePorSitioAplica(p: { viaje_por_sitio?: boolean | undefined }): boolean {
  return p.viaje_por_sitio === true;
}

/** ADENDA z68-3: ¿esta sucursal nació con el todo en uno con casas y bimestrales (formato consolidado)? Ausente = un proyecto
 *  guardado antes: su PDF de todas sigue EXACTO (formato multisitio, la casa y el bimestral fuera). */
export function consolidadoV1Aplica(p: { consolidado_v1?: boolean | undefined }): boolean {
  return p.consolidado_v1 === true;
}

/** Una sucursal (viva) de un multisitio, para repartir los viáticos. */
export interface SucursalViaticos {
  id: string;
  folio: string;
  sitio: string;
  /** La ciudad de la obra (una clave: municipio de Sonora, o la ciudad y el estado escritos) si es FUERA de Hermosillo; null =
   *  en Hermosillo (no lleva viáticos). */
  ciudad: string | null;
  /** Cómo se llama esa ciudad para el vendedor («Navojoa»). */
  nombre_ciudad: string;
  /** Lo que suman sus viáticos capturados (montoDeViaticos). */
  viaticos: number;
}
export type QuienLlevaViaticos = Pick<SucursalViaticos, 'id' | 'folio' | 'sitio'>;
export interface ViaticosPorCiudad {
  /** Por sucursal fuera de Hermosillo: la que lleva los viáticos de su ciudad (ella misma u otra). */
  quien_lleva: ReadonlyMap<string, QuienLlevaViaticos>;
  /** Ciudades en las que NINGUNA sucursal trae viáticos (el PDF de todas no sale). */
  faltan: { ciudad: string; sucursales: QuienLlevaViaticos[] }[];
  /** Ciudades en las que MÁS de una sucursal trae viáticos: se cobrarían dos veces (el PDF de todas no sale). */
  dobles: { ciudad: string; sucursales: (QuienLlevaViaticos & { viaticos: number })[] }[];
}

/** Diego, 27-oct (z46-7): «un viático por ciudad». Por cada ciudad fuera de Hermosillo, los lleva la sucursal de folio más bajo que
 *  los trae; si ninguna los trae, la de folio más bajo (ahí se le piden). Las demás de esa ciudad no los llevan. Pura: la usan el
 *  servidor (al calcular cada sucursal y al sacar el PDF de todas) y sus pruebas. */
export function viaticosPorCiudad(xs: readonly SucursalViaticos[]): ViaticosPorCiudad {
  const porCiudad = new Map<string, SucursalViaticos[]>();
  for (const x of xs) if (x.ciudad !== null) porCiudad.set(x.ciudad, [...(porCiudad.get(x.ciudad) ?? []), x]);
  const quien_lleva = new Map<string, QuienLlevaViaticos>();
  const faltan: ViaticosPorCiudad['faltan'] = [];
  const dobles: ViaticosPorCiudad['dobles'] = [];
  const corto = (x: SucursalViaticos): QuienLlevaViaticos => ({ id: x.id, folio: x.folio, sitio: x.sitio });
  for (const grupo of porCiudad.values()) {
    const orden = [...grupo].sort((a, b) => (a.folio < b.folio ? -1 : a.folio > b.folio ? 1 : a.id < b.id ? -1 : 1));
    const conViaticos = orden.filter((x) => x.viaticos > 0);
    const lleva = conViaticos[0] ?? orden[0]!;
    for (const x of orden) quien_lleva.set(x.id, corto(lleva));
    const nombre = orden[0]!.nombre_ciudad;
    if (!conViaticos.length) faltan.push({ ciudad: nombre, sucursales: orden.map(corto) });
    if (conViaticos.length > 1) dobles.push({ ciudad: nombre, sucursales: conViaticos.map((x) => ({ ...corto(x), viaticos: x.viaticos })) });
  }
  return { quien_lleva, faltan, dobles };
}

/** Cuerpo de POST /api/cotizaciones/:id/costos (el servidor pone id, capturado_por y capturado_en). */
export const CuerpoCosto = CostoAdicional.omit({ id: true, capturado_por: true, capturado_en: true });
export type CuerpoCosto = z.infer<typeof CuerpoCosto>;
